<template>
  <div class="se-wrap">
    <!-- 左侧：所有字段（拖拽源） -->
    <div class="se-left">
      <div class="se-section-title">{{ $t('字段') }}</div>
      <a-input
        class="se-field-search"
        v-model:value="fieldSearch"
        size="small"
        allow-clear
        :placeholder="$t('搜索字段')"
      >
        <template #prefix>
          <search-outlined />
        </template>
      </a-input>
      <div class="se-field-pool">
        <div
          v-for="f in displayFieldRows"
          :key="f.key"
          :class="['se-field-item', { 'se-field-group': !f.isLeaf }]"
          :style="{ paddingLeft: `${8 + f.depth * 16}px` }"
          :draggable="f.isLeaf"
          @dragstart="f.isLeaf ? onFieldDragStart(f.option) : undefined"
          @dragend="f.isLeaf ? onDragEnd() : undefined"
          :title="f.isLeaf ? `${f.option.label}(${f.option.field})` : f.option.label"
        >
          <database-outlined
            v-if="f.isLeaf"
            class="se-field-icon"
          />
          <span
            v-else
            class="se-field-branch-dot"
          />
          <span class="se-field-label">
            {{ f.isLeaf ? `${f.option.label}(${f.option.field})` : f.option.label }}
          </span>
        </div>
        <div
          v-if="displayFieldRows.length === 0"
          class="se-empty"
        >
          {{ $t('未找到匹配字段') }}
        </div>
      </div>
    </div>

    <!-- 右侧：表达式名称 + 表达式编辑器 -->
    <div class="se-right">
      <div class="se-name-row">
        <span class="se-name-label">{{ $t('表达式名称') }}</span>
        <a-input
          v-model:value="local.name"
          size="small"
          class="se-name-input"
          :placeholder="$t('请输入子表达式名称（将作为可拖入的引用名）')"
          @change="emitChange"
        />
      </div>
      <valueExprEditor
        v-model="local.expression"
        :field-options="flatFieldOptions"
        :sub-expressions="availableSubExprs"
        :exclude-sub-expr-id="local.id"
        :placeholder="$t('从左侧拖拽字段，或拖拽左侧「子表达式」名称，构建可复用的表达式片段')"
        @change="emitChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import { DatabaseOutlined, SearchOutlined } from '@ant-design/icons-vue';
import {
  PivotSubExpression,
  PivotFieldOption,
  ExprLocalDrag,
} from '../typing';
import ValueExprEditor from './valueExprEditor.vue';

interface Props {
  modelValue: PivotSubExpression;
  fieldOptions: PivotFieldOption[];
  // 当前配置中全部子表达式（供引用其它子表达式）；编辑自身时由 excludeSubExprId 排除
  subExpressions?: PivotSubExpression[];
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: PivotSubExpression): void;
  (e: 'change'): void;
}>();

// 跨子组件共享的拖拽负载（字段从左侧池拖出，落到编辑器画布）
const localDrag = ref<ExprLocalDrag | null>(null);
provide('vfLocalDrag', localDrag);

// 可引用子表达式：排除正在编辑的自身（避免直接自引用；递归展开由后端深度上限兜底）
const availableSubExprs = computed<PivotSubExpression[]>(() => {
  const list = props.subExpressions || [];
  if (props.modelValue?.id) {
    return list.filter(s => s.id !== props.modelValue.id);
  }
  return list;
});

const local = ref<PivotSubExpression>(cloneDeep(props.modelValue));
watch(
  () => props.modelValue,
  v => {
    if (v) local.value = cloneDeep(v);
  },
  { immediate: true, deep: false },
);

const flatFieldOptions = computed<PivotFieldOption[]>(() => {
  const flatten = (fields: PivotFieldOption[]): PivotFieldOption[] => {
    const list: PivotFieldOption[] = [];
    fields.forEach(field => {
      if (field.children?.length) {
        list.push(...flatten(field.children));
      } else {
        list.push({ field: field.field, label: field.label });
      }
    });
    return list;
  };
  return flatten(props.fieldOptions || []);
});

const fieldSearch = ref('');
const filteredFieldTree = computed<PivotFieldOption[]>(() => {
  const q = fieldSearch.value.trim().toLowerCase();
  if (!q) return props.fieldOptions;

  const filterTree = (fields: PivotFieldOption[]): PivotFieldOption[] => {
    return fields
      .map(field => {
        const selfMatch =
          field.label.toLowerCase().includes(q) || field.field.toLowerCase().includes(q);
        const children = field.children?.length ? filterTree(field.children) : [];
        if (selfMatch) {
          return {
            ...field,
            children: field.children?.length ? field.children : undefined,
          };
        }
        if (children.length) {
          return {
            ...field,
            children,
          };
        }
        if (!field.children?.length && selfMatch) {
          return { ...field };
        }
        return null;
      })
      .filter(Boolean) as PivotFieldOption[];
  };

  return filterTree(props.fieldOptions);
});

const displayFieldRows = computed<
  Array<{ key: string; depth: number; isLeaf: boolean; option: PivotFieldOption }>
>(() => {
  const list: Array<{ key: string; depth: number; isLeaf: boolean; option: PivotFieldOption }> = [];
  const walk = (fields: PivotFieldOption[], depth = 0) => {
    fields.forEach(field => {
      const isLeaf = !field.children?.length;
      list.push({
        key: `${depth}_${field.field}_${field.label}`,
        depth,
        isLeaf,
        option: field,
      });
      if (field.children?.length) {
        walk(field.children, depth + 1);
      }
    });
  };
  walk(filteredFieldTree.value);
  return list;
});

const emitChange = () => {
  emit('update:modelValue', cloneDeep(local.value));
  emit('change');
};

const onFieldDragStart = (f: PivotFieldOption) => {
  localDrag.value = { kind: 'field', field: f.field, label: f.label };
};
const onDragEnd = () => {
  localDrag.value = null;
};

// 对外暴露：是否具备可保存条件（名称非空）
defineExpose({
  isValid: () => !!(local.value.name && local.value.name.trim()),
});
</script>

<style lang="less" scoped>
.se-wrap {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 360px;
}

.se-left {
  width: 240px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .se-section-title {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 13px;
  }
  .se-field-search {
    margin-bottom: 8px;
    flex-shrink: 0;
  }
  .se-field-pool {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
  .se-field-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    margin-bottom: 4px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    cursor: grab;
    font-size: 13px;
    transition: all 0.2s;
    user-select: none;
    &:hover {
      border-color: #1890ff;
      background: #f0f7ff;
    }
    &:active {
      cursor: grabbing;
    }
    .se-field-icon {
      color: #1890ff;
      font-size: 12px;
    }
    .se-field-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .se-field-group {
    cursor: default;
    background: #fafafa;
    font-weight: 600;
    &:hover {
      border-color: #f0f0f0;
      background: #fafafa;
    }
  }
  .se-field-branch-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d9d9d9;
    display: inline-block;
    flex-shrink: 0;
  }
  .se-empty {
    text-align: center;
    color: #999;
    font-size: 12px;
    padding: 20px 0;
  }
}

.se-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;

  .se-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    .se-name-label {
      font-size: 13px;
      color: #333;
      flex-shrink: 0;
      font-weight: 600;
    }
    .se-name-input {
      flex: 1;
      max-width: 360px;
    }
  }
}
</style>
